import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { createConsumer } from '@rails/actioncable'
import { getCableUrl } from '../cable'
import { NOTIFICATION_PUSH } from '../redux/Action/actionTypes'
import { fetchMyProposalsAction, fetchMyAssignmentsAction } from '../redux/Action/Campaign/campaignAction'
/**
 * Subscribes to the user's private NotificationsChannel so every notification
 * is pushed instantly to the Redux store (bell badge + list).
 *
 * Also triggers background data re-fetches for role-specific notifications so
 * pages like CreatorOverview and AgencyOverview stay current without a reload.
 * Campaign-specific pages (ProposalsPage, BrandTrackingView, CampaignProgressView)
 * handle their own live data via useCampaignActivity.
 *
 * Mount once at the app level (App.js inside the Router).
 * Push notification setup lives in NotificationBell — only one hook instance.
 */
export const useNotifications = () => {
  const dispatch = useDispatch()
  const { user } = useSelector((s) => s.getMe)

  useEffect(() => {
    if (!user?.id) return

    const consumer = createConsumer(() => {
      const token = localStorage.getItem('token')
      if (!token) return getCableUrl()
      return `${getCableUrl()}?token=${encodeURIComponent(token)}`
    })

    const subscription = consumer.subscriptions.create(
      { channel: 'NotificationsChannel' },
      {
        connected() {
          // Ping every 4 minutes to keep server-side presence fresh (TTL is 5 min)
          this._pingInterval = setInterval(() => this.perform('ping'), 4 * 60 * 1000)
        },
        disconnected() {
          clearInterval(this._pingInterval)
        },
        rejected() {
          console.warn('[Notifications] ❌ Subscription rejected')
          clearInterval(this._pingInterval)
          consumer.disconnect()
        },
        received(data) {
          if (data.event !== 'new_notification' || !data.notification) return
          const notif = data.notification
          dispatch({ type: NOTIFICATION_PUSH, payload: notif })

          if (notif.title) {
            toast.info(notif.title + (notif.message ? `: ${notif.message}` : ''), {
              toastId: `notif-${notif.id || Date.now()}`,
              autoClose: 4000
            })
          }

          // Background re-fetches so role overview pages update without reload.
          // Campaign-specific pages handle finer-grained updates via useCampaignActivity.
          const type = notif.type || ''

          if (user.role === 'creator') {
            if (['proposal_accepted', 'proposal_rejected', 'campaign_cancelled', 'campaign_under_review'].includes(type)) {
              dispatch(fetchMyProposalsAction())
            }
            if (['assignment_created', 'assignment_approved', 'assignment_revision_requested'].includes(type)) {
              dispatch(fetchMyAssignmentsAction())
            }
          }

          if (user.role === 'agency') {
            if (['proposal_accepted', 'proposal_rejected', 'campaign_cancelled'].includes(type)) {
              dispatch(fetchMyProposalsAction())
            }
          }
        },
      }
    )

    return () => {
      subscription.unsubscribe()
      consumer.disconnect()
    }
  }, [user?.id, user?.role, dispatch])
}
